import { USERS_LIST_DEFAULT_PAGE_SIZE } from "@/features/tenantUsers/usersList";
import type { DataTableColumn } from "@/shared/components/common/DataTable";
import type { TeacherListRow, TeacherAssignment } from "../types/teacher.types";

export function getTeachersListingConfig(t: (key: string) => string) {
  return {
    entityLabel: t("teachers:list.entityLabel"),
    basePath: "/teachers",
    searchPlaceholder: t("teachers:list.searchPlaceholder"),
    sampleSheetPath: "/teachers/bulk-upload",
    bulkUploadPath: "/teachers/bulk-upload",
    addPath: "/teachers/add-teachers",
    addLabel: t("teachers:list.addTeacher"),
    pageSize: USERS_LIST_DEFAULT_PAGE_SIZE,
  } as const;
}

/**
 * Groups assignments by class+section and returns a compact display string.
 * e.g. "10-A, 9-B"
 * NOTE: Subjects are commented out below — uncomment to show them.
 * e.g. "10-A (Mathematics, Science), 9-B (Mathematics)"
 */
function formatAssignments(assignments: TeacherAssignment[]): string {
  if (!assignments || assignments.length === 0) return "";

  // Group by "class-section"
  const grouped = new Map<string, string[]>();
  for (const a of assignments) {
    const classLabel = a.class || "";
    const sectionLabel = a.section || "";
    const key = sectionLabel ? `${classLabel}-${sectionLabel}` : classLabel;
    if (!key) continue;
    const subjects = grouped.get(key) ?? [];
    if (a.subject) subjects.push(a.subject);
    grouped.set(key, subjects);
  }

  const parts: string[] = [];
  for (const [key, _subjects] of grouped) {
    // Uncomment below to show subjects in the table:
    // if (_subjects.length > 0) {
    //   parts.push(`${key} (${_subjects.join(", ")})`);
    // } else {
    //   parts.push(key);
    // }
    parts.push(key);
  }
  return parts.join(", ");
}

/**
 * Distinct boards for a teacher, drawn from their assignments (a teacher can
 * teach across multiple boards, e.g. "CBSE, ICSE"), falling back to the
 * top-level board field.
 */
function formatBoards(row: TeacherListRow): string {
  const boards = new Set<string>();
  for (const a of row.assignments ?? []) {
    const b = (a.board ?? "").trim();
    if (b) boards.add(b);
  }
  if (boards.size === 0 && row.board?.trim()) {
    boards.add(row.board.trim());
  }
  return boards.size > 0 ? Array.from(boards).join(", ") : "—";
}

/**
 * Distinct sections for a teacher (the top-level `section`, e.g. `section.name`, or the
 * sections of their assignments).
 */
function formatSections(row: TeacherListRow): string {
  const sections = new Set<string>();
  if (row.section?.trim()) sections.add(row.section.trim());
  for (const a of row.assignments ?? []) {
    const s = (a.section ?? "").trim();
    if (s) sections.add(s);
  }
  return sections.size > 0 ? Array.from(sections).join(", ") : "—";
}

export function getTeachersTableColumns(
  t: (key: string) => string,
): DataTableColumn<TeacherListRow>[] {
  return [
    { key: "name", header: t("teachers:table.name") },
    { key: "email", header: t("teachers:table.email") },
    { key: "phone", header: t("teachers:table.phone") },
    {
      key: "board",
      header: t("teachers:table.board"),
      cell: <R extends TeacherListRow>(row: R) => formatBoards(row),
    },
    {
      key: "class",
      header: t("teachers:table.class"),
      cell: <R extends TeacherListRow>(row: R) => {
        if (row.assignments && row.assignments.length > 0) {
          return formatAssignments(row.assignments);
        }
        // Fallback to flat class field for legacy data
        return row.class || "—";
      },
    },
    {
      key: "section",
      header: t("teachers:table.section"),
      cell: <R extends TeacherListRow>(row: R) => formatSections(row),
    },
    { key: "accountStatus", header: t("teachers:table.accountStatus") },
  ];
}
